import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { RealtimeChannel } from "@supabase/supabase-js";
// Storage access that cannot throw — this provider wraps the public
// /embed/* routes, where reading window.localStorage raises a SecurityError.
import { safeGetItem } from "@mediatechgroup/app-version";
import { AuthContext } from "./AuthContext";
import { getTenantId, getAuthSource, getAuthToken } from "../api/apiHelpers";
import axios from "axios";
import {
  ChatConversation,
  ChatMessage,
  ChatParticipant,
  UserInfo,
  SupportCategory,
  getUserConversations,
  getOrCreateDMConversation,
  getConversationMessages,
  sendMessage as sendMessageAPI,
  markConversationRead,
  updateTypingStatus,
  subscribeToMessages,
  subscribeToTyping,
  subscribeToUserConversations,
  unsubscribe,
  checkSupportAvailability,
  startSupportChat,
  archiveConversation as archiveConversationAPI,
  getArchivedConversations,
} from "../api/chatAPI";
import { parentSupabase } from "../utils/supabaseClient";
import { submitSupportTicket } from "../api/supportTicketAPI";
import { captureException } from '@sentry/react';

// ============================================
// TYPES
// ============================================

// Searchable user from tenant database
export interface SearchableUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
  role?: string;
}

interface ChatContextType {
  // State
  conversations: ChatConversation[];
  archivedConversations: ChatConversation[];
  activeConversation: ChatConversation | null;
  messages: ChatMessage[];
  isLoading: boolean;
  isWidgetOpen: boolean;
  isWidgetMinimized: boolean;
  unreadCount: number;
  typingUsers: ChatParticipant[];
  supportAvailable: boolean;
  suggestTicket: boolean;
  isWaitingForAgent: boolean;
  showInChatTicketForm: boolean;
  showCategorySelector: boolean;
  selectedCategory: SupportCategory | null;
  /**
   * A prompt handed to the staff AI assistant from elsewhere in the app
   * (e.g. the global search bar's "Not finding what you're looking for?"
   * row). ChatWidget watches `assistantPromptNonce` and, when it changes,
   * opens the AI assistant view with `assistantPrompt` prefilled into the
   * composer (a fresh conversation; not auto-sent).
   */
  assistantPrompt: string | null;
  assistantPromptNonce: number;

  // Actions
  openWidget: () => void;
  closeWidget: () => void;
  minimizeWidget: () => void;
  maximizeWidget: () => void;
  selectConversation: (conversation: ChatConversation | null) => void;
  startDMWithUser: (otherUser: UserInfo) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  setTyping: (isTyping: boolean) => void;
  refreshConversations: () => Promise<void>;
  openSupportCategorySelector: () => void;
  selectSupportCategory: (category: SupportCategory) => void;
  cancelSupportCategorySelection: () => void;
  startSupportChatSession: (category?: SupportCategory) => Promise<void>;
  cancelWaitingForAgent: () => void;
  submitInChatTicket: (subject: string, category: string, message: string) => Promise<boolean>;
  searchUsers: (query: string) => Promise<SearchableUser[]>;
  getCurrentUserInfo: () => UserInfo | null;
  archiveConversation: (conversationId: string) => Promise<void>;
  unarchiveConversation: (conversationId: string) => Promise<void>;
  fetchArchivedConversations: () => Promise<void>;
  /** Open the AI assistant with `prompt` prefilled in a fresh conversation. */
  askAssistant: (prompt: string) => void;
  /** Clear the pending assistant prompt (after ChatWidget has consumed it). */
  clearAssistantPrompt: () => void;
}

const defaultContext: ChatContextType = {
  conversations: [],
  archivedConversations: [],
  activeConversation: null,
  messages: [],
  isLoading: false,
  isWidgetOpen: false,
  isWidgetMinimized: false,
  unreadCount: 0,
  typingUsers: [],
  supportAvailable: false,
  suggestTicket: false,
  isWaitingForAgent: false,
  showInChatTicketForm: false,
  showCategorySelector: false,
  selectedCategory: null,
  assistantPrompt: null,
  assistantPromptNonce: 0,

  openWidget: () => {},
  closeWidget: () => {},
  minimizeWidget: () => {},
  maximizeWidget: () => {},
  selectConversation: () => {},
  startDMWithUser: async () => {},
  sendMessage: async () => {},
  setTyping: () => {},
  refreshConversations: async () => {},
  openSupportCategorySelector: () => {},
  selectSupportCategory: () => {},
  cancelSupportCategorySelection: () => {},
  startSupportChatSession: async () => {},
  cancelWaitingForAgent: () => {},
  submitInChatTicket: async () => false,
  searchUsers: async () => [],
  getCurrentUserInfo: () => null,
  archiveConversation: async () => {},
  unarchiveConversation: async () => {},
  fetchArchivedConversations: async () => {},
  askAssistant: () => {},
  clearAssistantPrompt: () => {},
};

export const ChatContext = createContext<ChatContextType>(defaultContext);

// ============================================
// PROVIDER
// ============================================

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { user } = useContext(AuthContext);

  // Get tenant info from URL - use the slug directly as tenant identifier
  const tenantSlug = getTenantId();
  // Use slug directly instead of looking up UUID (the tenants table uses integer IDs, not UUIDs)
  const tenantId = tenantSlug || null;

  // State
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [archivedConversations, setArchivedConversations] = useState<ChatConversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [isWidgetMinimized, setIsWidgetMinimized] = useState(false);
  const [typingUsers, setTypingUsers] = useState<ChatParticipant[]>([]);
  const [supportAvailable, setSupportAvailable] = useState(false);
  const [suggestTicket, setSuggestTicket] = useState(false);
  const [isWaitingForAgent, setIsWaitingForAgent] = useState(false);
  const [showInChatTicketForm, setShowInChatTicketForm] = useState(false);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<SupportCategory | null>(null);
  const [assistantPrompt, setAssistantPrompt] = useState<string | null>(null);
  const [assistantPromptNonce, setAssistantPromptNonce] = useState(0);

  // Refs for subscriptions
  const messageChannelRef = useRef<RealtimeChannel | null>(null);
  const typingChannelRef = useRef<RealtimeChannel | null>(null);
  const userChannelRef = useRef<RealtimeChannel | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const agentWaitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const supportParticipantChannelRef = useRef<RealtimeChannel | null>(null);

  // Computed
  const unreadCount = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);

  // Get current user info
  const getCurrentUserInfo = useCallback((): UserInfo | null => {
    const authSource = getAuthSource();

    // For central auth users (platform admin, tenant owner), get user from localStorage
    if (authSource === "central" || authSource === "tenant_owner") {
      const userDetailsStr = safeGetItem("userDetails");
      if (userDetailsStr) {
        try {
          const userDetails = JSON.parse(userDetailsStr);
          const displayName = userDetails.first_name && userDetails.last_name
            ? `${userDetails.first_name} ${userDetails.last_name}`
            : userDetails.email?.split("@")[0] || "User";

          return {
            id: userDetails.id,
            type: "platform_user", // Both central and tenant_owner auth use platform_user type
            tenant_id: tenantId,
            display_name: displayName,
          };
        } catch (e) {
          captureException(e);
        }
      }
      return null;
    }

    // For tenant auth users, get user from AuthContext (Supabase session)
    if (!user) return null;

    // Get display name from user metadata or email
    const displayName =
      user.user_metadata?.first_name && user.user_metadata?.last_name
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
        : user.email?.split("@")[0] || "User";

    return {
      id: user.id,
      type: "tenant_user",
      tenant_id: tenantId,
      display_name: displayName,
    };
  }, [user, tenantId]);

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const refreshConversations = useCallback(async () => {
    const currentUser = getCurrentUserInfo();
    if (!currentUser) return;

    setIsLoading(true);
    try {
      const result = await getUserConversations(currentUser.id, tenantId || undefined);
      if (result.success) {
        setConversations(result.data);
      }
    } catch (error) {
      // Errors captured by axios interceptor via chatAPI
    } finally {
      setIsLoading(false);
    }
  }, [getCurrentUserInfo, tenantId]);

  const fetchMessages = useCallback(async (conversationId: string) => {
    setIsLoading(true);
    try {
      const result = await getConversationMessages(conversationId);
      if (result.success) {
        setMessages(result.data);
      }
    } catch (error) {
      // Errors captured by axios interceptor via chatAPI
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkSupport = useCallback(async () => {
    const result = await checkSupportAvailability();
    setSupportAvailable(result.available);
    setSuggestTicket(result.suggestTicket);
  }, []);

  // ============================================
  // SUBSCRIPTION MANAGEMENT
  // ============================================

  const setupMessageSubscription = useCallback(
    (conversationId: string) => {
      // Cleanup existing subscription
      if (messageChannelRef.current) {
        unsubscribe(messageChannelRef.current);
      }

      // Subscribe to new messages
      messageChannelRef.current = subscribeToMessages(conversationId, (newMessage) => {
        setMessages((prev) => [...prev, newMessage]);

        // Update conversation's last_message
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? { ...conv, last_message: newMessage, updated_at: newMessage.created_at }
              : conv
          )
        );
      });
    },
    []
  );

  const setupTypingSubscription = useCallback(
    (conversationId: string) => {
      if (!user) return;

      // Cleanup existing subscription
      if (typingChannelRef.current) {
        unsubscribe(typingChannelRef.current);
      }

      // Subscribe to typing indicators
      typingChannelRef.current = subscribeToTyping(
        conversationId,
        user.id,
        (typingParticipants) => {
          setTypingUsers(typingParticipants);
        }
      );
    },
    [user]
  );

  const setupUserSubscription = useCallback(() => {
    if (!user) return;

    // Cleanup existing subscription
    if (userChannelRef.current) {
      unsubscribe(userChannelRef.current);
    }

    // Subscribe to all user's conversations
    userChannelRef.current = subscribeToUserConversations(
      user.id,
      // On new message in any conversation
      (conversationId, message) => {
        // Check if the conversation is in the archived list and move it to active
        // (Backend automatically unarchives on new message, we sync the frontend state)
        setArchivedConversations((prevArchived) => {
          const archivedConv = prevArchived.find(c => c.id === conversationId);
          if (archivedConv) {
            // Move from archived to active conversations
            setConversations((prevActive) => {
              const isActive = activeConversation?.id === conversationId;
              const updatedConv = {
                ...archivedConv,
                last_message: message,
                updated_at: message.created_at,
                unread_count: isActive ? 0 : (archivedConv.unread_count || 0) + 1,
              };
              // Add to top of active list if not already there
              if (prevActive.some(c => c.id === conversationId)) {
                return prevActive.map(c => c.id === conversationId ? updatedConv : c);
              }
              return [updatedConv, ...prevActive];
            });
            // Remove from archived
            return prevArchived.filter(c => c.id !== conversationId);
          }
          return prevArchived;
        });

        // Update conversations list with new message
        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.id === conversationId) {
              const isActive = activeConversation?.id === conversationId;
              return {
                ...conv,
                last_message: message,
                updated_at: message.created_at,
                unread_count: isActive ? 0 : (conv.unread_count || 0) + 1,
              };
            }
            return conv;
          })
        );

        // If this is the active conversation, add message to list
        if (activeConversation?.id === conversationId) {
          setMessages((prev) => {
            // Avoid duplicates
            if (prev.some((m) => m.id === message.id)) return prev;
            return [...prev, message];
          });
        }
      },
      // On conversation update
      (updatedConv) => {
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === updatedConv.id ? { ...conv, ...updatedConv } : conv
          )
        );
      }
    );
  }, [user, activeConversation]);

  // ============================================
  // ACTIONS
  // ============================================

  const openWidget = useCallback(() => {
    setIsWidgetOpen(true);
    setIsWidgetMinimized(false);
    // Check support availability when widget opens (instead of polling)
    checkSupport();
  }, [checkSupport]);

  // Hand a prompt to the staff AI assistant (from the global search bar's
  // "Not finding…" row). Bumping the nonce is what ChatWidget watches — it
  // makes repeat asks with the same text still trigger. We open the widget
  // here so the caller just needs the one call.
  const askAssistant = useCallback((prompt: string) => {
    setAssistantPrompt(prompt);
    setAssistantPromptNonce((n) => n + 1);
    setIsWidgetOpen(true);
    setIsWidgetMinimized(false);
    checkSupport();
  }, [checkSupport]);

  const clearAssistantPrompt = useCallback(() => {
    setAssistantPrompt(null);
  }, []);

  const closeWidget = useCallback(() => {
    setIsWidgetOpen(false);
    setActiveConversation(null);
    setMessages([]);
    setTypingUsers([]);
  }, []);

  const minimizeWidget = useCallback(() => {
    setIsWidgetMinimized(true);
  }, []);

  const maximizeWidget = useCallback(() => {
    setIsWidgetMinimized(false);
  }, []);

  const selectConversation = useCallback(
    async (conversation: ChatConversation | null) => {
      setActiveConversation(conversation);

      if (conversation) {
        // Fetch messages
        await fetchMessages(conversation.id);

        // Mark as read
        const currentUser = getCurrentUserInfo();
        if (currentUser) {
          await markConversationRead(conversation.id, currentUser.id);

          // Update local unread count
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === conversation.id ? { ...conv, unread_count: 0 } : conv
            )
          );
        }

        // Setup subscriptions
        setupMessageSubscription(conversation.id);
        setupTypingSubscription(conversation.id);
      } else {
        setMessages([]);
        setTypingUsers([]);

        // Cleanup subscriptions
        if (messageChannelRef.current) {
          unsubscribe(messageChannelRef.current);
          messageChannelRef.current = null;
        }
        if (typingChannelRef.current) {
          unsubscribe(typingChannelRef.current);
          typingChannelRef.current = null;
        }
      }
    },
    [getCurrentUserInfo, fetchMessages, setupMessageSubscription, setupTypingSubscription]
  );

  const startDMWithUser = useCallback(
    async (otherUser: UserInfo) => {
      const currentUser = getCurrentUserInfo();
      if (!currentUser) {
        return;
      }

      setIsLoading(true);
      try {
        const result = await getOrCreateDMConversation(
          currentUser,
          otherUser,
          tenantId || undefined
        );

        if (result.success && result.data) {
          // Add to conversations if not already there
          setConversations((prev) => {
            const exists = prev.some((c) => c.id === result.data!.id);
            if (exists) return prev;
            return [result.data!, ...prev];
          });

          // Select the conversation
          await selectConversation(result.data);
          openWidget();
        }
      } catch (error) {
        // Errors captured by axios interceptor via chatAPI
      } finally {
        setIsLoading(false);
      }
    },
    [getCurrentUserInfo, tenantId, selectConversation, openWidget]
  );

  const sendMessage = useCallback(
    async (content: string) => {
      if (!activeConversation || !content.trim()) return;

      const currentUser = getCurrentUserInfo();
      if (!currentUser) return;

      try {
        const result = await sendMessageAPI(
          activeConversation.id,
          currentUser.id,
          currentUser.type,
          content.trim()
        );

        if (result.success && result.data) {
          // Message will be added via realtime subscription
          // But we can optimistically add it
          setMessages((prev) => {
            if (prev.some((m) => m.id === result.data!.id)) return prev;
            return [...prev, result.data!];
          });
        }

        // Clear typing indicator
        setTyping(false);
      } catch (error) {
        // Errors captured by axios interceptor via chatAPI
      }
    },
    [activeConversation, getCurrentUserInfo]
  );

  const setTyping = useCallback(
    (isTyping: boolean) => {
      const currentUser = getCurrentUserInfo();
      if (!activeConversation || !currentUser) return;

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Update typing status
      updateTypingStatus(activeConversation.id, currentUser.id, isTyping);

      // Auto-clear typing after 3 seconds
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(() => {
          updateTypingStatus(activeConversation.id, currentUser.id, false);
        }, 3000);
      }
    },
    [activeConversation, getCurrentUserInfo]
  );

  // Category selection functions
  const openSupportCategorySelector = useCallback(() => {
    setShowCategorySelector(true);
    setSelectedCategory(null);
    openWidget();
  }, []);

  const cancelSupportCategorySelection = useCallback(() => {
    setShowCategorySelector(false);
    setSelectedCategory(null);
  }, []);

  const startSupportChatSession = useCallback(async (category: SupportCategory = "technical") => {
    const currentUser = getCurrentUserInfo();
    if (!currentUser || !tenantId) return;

    // Reset states
    setShowInChatTicketForm(false);
    setShowCategorySelector(false);
    setIsWaitingForAgent(true);
    setIsLoading(true);

    try {
      const result = await startSupportChat(currentUser, tenantId, category);

      if (result.success && result.data) {
        const conversation = result.data;

        // Add to conversations
        setConversations((prev) => [conversation, ...prev]);

        // Select it
        await selectConversation(conversation);
        openWidget();

        // Determine message based on routing
        const routing = result.routing;
        const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);
        let connectingContent = `Connecting you with support for ${categoryLabel}...`;

        // If routed to tenant staff and staff are already added, show different message
        if (routing?.routeTo === "tenant" && routing?.staffCount > 0) {
          connectingContent = `Your ${categoryLabel} support request has been sent to ${routing.staffCount} team member${routing.staffCount > 1 ? "s" : ""}. They will respond shortly.`;
          // Don't wait for agent if tenant staff are already added
          setIsWaitingForAgent(false);
        }

        // Add a "connecting" system message locally
        const connectingMessage: ChatMessage = {
          id: `system-connecting-${Date.now()}`,
          conversation_id: conversation.id,
          sender_id: "system",
          sender_type: "system",
          message_type: "system",
          content: connectingContent,
          attachments: [],
          reply_to_id: null,
          created_at: new Date().toISOString(),
          edited_at: null,
          is_deleted: false,
        };
        setMessages((prev) => [...prev, connectingMessage]);

        // If routed to tenant staff (who are already added), don't set up agent wait timeout
        if (routing?.routeTo === "tenant" && routing?.staffCount > 0) {
          setIsLoading(false);
          return;
        }

        // DISABLED: Realtime subscriptions on the central/parent DB were exhausting
        // the PostgreSQL connection pool. Agent detection disabled until Realtime is stabilized.
        // supportParticipantChannelRef.current = parentSupabase.channel(...).subscribe();

        // Start 30-second timeout
        agentWaitTimeoutRef.current = setTimeout(() => {
          setIsWaitingForAgent(false);
          setShowInChatTicketForm(true);

          // Add a timeout message
          const timeoutMessage: ChatMessage = {
            id: `system-timeout-${Date.now()}`,
            conversation_id: conversation.id,
            sender_id: "system",
            sender_type: "system",
            message_type: "system",
            content: "No agents are available right now. Please submit a support ticket and we'll get back to you as soon as possible.",
            attachments: [],
            reply_to_id: null,
            created_at: new Date().toISOString(),
            edited_at: null,
            is_deleted: false,
          };
          setMessages((prev) => [...prev, timeoutMessage]);

          // Cleanup subscription
          if (supportParticipantChannelRef.current) {
            parentSupabase.removeChannel(supportParticipantChannelRef.current);
            supportParticipantChannelRef.current = null;
          }
        }, 30000); // 30 seconds
      }
    } catch (error) {
      // Errors captured by axios interceptor via chatAPI
      setIsWaitingForAgent(false);
    } finally {
      setIsLoading(false);
    }
  }, [getCurrentUserInfo, tenantId, selectConversation, openWidget]);

  const selectSupportCategory = useCallback(async (category: SupportCategory) => {
    setSelectedCategory(category);
    // Start the chat session with the selected category
    await startSupportChatSession(category);
  }, [startSupportChatSession]);

  const cancelWaitingForAgent = useCallback(() => {
    if (agentWaitTimeoutRef.current) {
      clearTimeout(agentWaitTimeoutRef.current);
      agentWaitTimeoutRef.current = null;
    }
    if (supportParticipantChannelRef.current) {
      parentSupabase.removeChannel(supportParticipantChannelRef.current);
      supportParticipantChannelRef.current = null;
    }
    setIsWaitingForAgent(false);
    setShowInChatTicketForm(false);
  }, []);

  const submitInChatTicket = useCallback(async (
    subject: string,
    category: string,
    message: string
  ): Promise<boolean> => {
    const currentUser = getCurrentUserInfo();
    if (!currentUser) return false;

    try {
      const token = getAuthToken();
      const authSource = getAuthSource();
      const currentTenantId = getTenantId();

      // Get tenant name from settings
      let tenantName = "";
      const settingsStr = safeGetItem("settings");
      if (settingsStr) {
        try {
          const settingsData = JSON.parse(settingsStr);
          tenantName = settingsData.name || settingsData.club_name || "";
        } catch (e) {
          captureException(e);
        }
      }

      // Get email - for central/tenant_owner auth, it's in localStorage, not AuthContext
      let userEmail = user?.email || "";
      if (!userEmail && (authSource === "central" || authSource === "tenant_owner")) {
        const userDetailsStr = safeGetItem("userDetails");
        if (userDetailsStr) {
          try {
            const userDetails = JSON.parse(userDetailsStr);
            userEmail = userDetails.email || "";
          } catch (e) {
            captureException(e);
          }
        }
      }

      const result = await submitSupportTicket(token, {
        name: currentUser.display_name,
        email: userEmail,
        subject: subject,
        category: category,
        priority: "medium",
        description: message,
        role: safeGetItem("userRole") || "member",
        tenantId: currentTenantId || "",
        tenantName: tenantName,
        userId: currentUser.id,
        authSource: authSource || "tenant",
      });

      if (result.success) {
        // Add a success message to the chat
        if (activeConversation) {
          const successMessage: ChatMessage = {
            id: `system-ticket-created-${Date.now()}`,
            conversation_id: activeConversation.id,
            sender_id: "system",
            sender_type: "system",
            message_type: "system",
            content: `Your support ticket has been created successfully. Our team will review it and respond as soon as possible. You can view your tickets in the Support Center.`,
            attachments: [],
            reply_to_id: null,
            created_at: new Date().toISOString(),
            edited_at: null,
            is_deleted: false,
          };
          setMessages((prev) => [...prev, successMessage]);
        }

        setShowInChatTicketForm(false);
        return true;
      }
      return false;
    } catch (error) {
      // Errors captured by axios interceptor via supportTicketAPI
      return false;
    }
  }, [getCurrentUserInfo, user, activeConversation]);

  const searchUsers = useCallback(async (query: string): Promise<SearchableUser[]> => {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const token = getAuthToken();
      if (!token) {
        return [];
      }

      const BASE_URL = process.env.REACT_APP_API_URL || "";
      const tenantId = getTenantId();
      const authSource = getAuthSource();
      const currentUserInfo = getCurrentUserInfo();
      const currentUserId = currentUserInfo?.id || user?.id;

      const headers = {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
        "x-tenant-id": tenantId,
        "x-auth-source": authSource,
      };

      // Fetch members, staff, and tenant owners in parallel
      const [membersResponse, staffResponse, tenantOwnersResponse] = await Promise.all([
        // Fetch members
        axios.get(`${BASE_URL}/memberships/all/members`, {
          headers,
          params: {
            page: 1,
            limit: 20,
            searchTerm: query,
            status: "active",
          },
        }).catch(() => {
          return { data: { data: [] } };
        }),
        // Fetch staff users (admins, coaches, etc.)
        axios.get(`${BASE_URL}/users/staff`, {
          headers,
        }).catch(() => {
          return { data: { data: [] } };
        }),
        // Fetch tenant owners (users with central auth access to this tenant)
        axios.get(`${BASE_URL}/users/tenant-owners`, {
          headers,
        }).catch(() => {
          return { data: { data: [] } };
        }),
      ]);

      const memberships = membersResponse.data?.data || membersResponse.data?.memberships || [];
      const staffUsers = staffResponse.data?.data || [];
      const tenantOwners = tenantOwnersResponse.data?.data || [];

      // Map memberships to SearchableUser format
      const memberUsers: SearchableUser[] = memberships.map((m: any) => ({
        id: m.id,
        first_name: m.first_name || "",
        last_name: m.last_name || "",
        email: m.email || "",
        avatar_url: m.avatar_url,
        role: m.role || "member",
      }));

      // Map staff users to SearchableUser format, filtering by search query
      const queryLower = query.toLowerCase();
      const staffSearchUsers: SearchableUser[] = staffUsers
        .filter((s: any) => {
          const fullName = `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase();
          const email = (s.email || "").toLowerCase();
          return fullName.includes(queryLower) || email.includes(queryLower);
        })
        .map((s: any) => ({
          id: s.id,
          first_name: s.first_name || "",
          last_name: s.last_name || "",
          email: s.email || "",
          avatar_url: s.avatar_url,
          role: s.role || "staff",
        }));

      // Map tenant owners to SearchableUser format, filtering by search query
      const tenantOwnerUsers: SearchableUser[] = tenantOwners
        .filter((o: any) => {
          const fullName = `${o.first_name || ""} ${o.last_name || ""}`.toLowerCase();
          const email = (o.email || "").toLowerCase();
          return fullName.includes(queryLower) || email.includes(queryLower);
        })
        .map((o: any) => ({
          id: o.id,
          first_name: o.first_name || "",
          last_name: o.last_name || "",
          email: o.email || "",
          avatar_url: o.avatar_url,
          role: "owner",
        }));

      // Combine and deduplicate by id, excluding current user
      // Priority: tenant owners first, then staff, then members
      const seenIds = new Set<string>();
      const combinedUsers: SearchableUser[] = [];

      for (const u of [...tenantOwnerUsers, ...staffSearchUsers, ...memberUsers]) {
        if (!seenIds.has(u.id) && u.id !== currentUserId) {
          seenIds.add(u.id);
          combinedUsers.push(u);
        }
      }

      // Sort by name
      combinedUsers.sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      return combinedUsers.slice(0, 20); // Limit to 20 results
    } catch (error: any) {
      captureException(error);
      return [];
    }
  }, [user]);

  // ============================================
  // ARCHIVE FUNCTIONS
  // ============================================

  const fetchArchivedConversations = useCallback(async () => {
    const currentUser = getCurrentUserInfo();
    if (!currentUser) return;

    try {
      const result = await getArchivedConversations(currentUser.id, tenantId || undefined);
      if (result.success) {
        setArchivedConversations(result.data);
      }
    } catch (error) {
      // Errors captured by axios interceptor via chatAPI
    }
  }, [getCurrentUserInfo, tenantId]);

  const archiveConversation = useCallback(async (conversationId: string) => {
    const currentUser = getCurrentUserInfo();
    if (!currentUser) return;

    try {
      const result = await archiveConversationAPI(conversationId, currentUser.id, true);
      if (result.success) {
        // Move conversation from active to archived
        const conversationToArchive = conversations.find(c => c.id === conversationId);
        if (conversationToArchive) {
          setConversations(prev => prev.filter(c => c.id !== conversationId));
          setArchivedConversations(prev => [conversationToArchive, ...prev]);
        }

        // If this was the active conversation, deselect it
        if (activeConversation?.id === conversationId) {
          setActiveConversation(null);
          setMessages([]);
        }
      }
    } catch (error) {
      // Errors captured by axios interceptor via chatAPI
    }
  }, [getCurrentUserInfo, conversations, activeConversation]);

  const unarchiveConversation = useCallback(async (conversationId: string) => {
    const currentUser = getCurrentUserInfo();
    if (!currentUser) return;

    try {
      const result = await archiveConversationAPI(conversationId, currentUser.id, false);
      if (result.success) {
        // Move conversation from archived to active
        const conversationToUnarchive = archivedConversations.find(c => c.id === conversationId);
        if (conversationToUnarchive) {
          setArchivedConversations(prev => prev.filter(c => c.id !== conversationId));
          setConversations(prev => [conversationToUnarchive, ...prev]);
        }
      }
    } catch (error) {
      // Errors captured by axios interceptor via chatAPI
    }
  }, [getCurrentUserInfo, archivedConversations]);

  // ============================================
  // EFFECTS
  // ============================================

  // Fetch conversations when user logs in
  // Note: We check getCurrentUserInfo() instead of just user because
  // tenant_owner auth doesn't use Supabase session (user from AuthContext)
  // but instead stores user info in localStorage
  useEffect(() => {
    const currentUser = getCurrentUserInfo();
    if (currentUser) {
      refreshConversations();
      checkSupport();
      // Only setup user subscription if we have a Supabase user (for realtime)
      if (user) {
        setupUserSubscription();
      }
    } else {
      setConversations([]);
      setActiveConversation(null);
      setMessages([]);
    }

    return () => {
      // Cleanup all subscriptions
      if (messageChannelRef.current) unsubscribe(messageChannelRef.current);
      if (typingChannelRef.current) unsubscribe(typingChannelRef.current);
      if (userChannelRef.current) unsubscribe(userChannelRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [user, refreshConversations, checkSupport]);

  // Support availability is now checked when the widget opens (see openWidget)
  // instead of polling every 60 seconds to reduce unnecessary API calls

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value: ChatContextType = {
    conversations,
    archivedConversations,
    activeConversation,
    messages,
    isLoading,
    isWidgetOpen,
    isWidgetMinimized,
    unreadCount,
    typingUsers,
    supportAvailable,
    suggestTicket,
    isWaitingForAgent,
    showInChatTicketForm,
    showCategorySelector,
    selectedCategory,
    assistantPrompt,
    assistantPromptNonce,

    openWidget,
    closeWidget,
    minimizeWidget,
    maximizeWidget,
    selectConversation,
    startDMWithUser,
    sendMessage,
    setTyping,
    refreshConversations,
    openSupportCategorySelector,
    selectSupportCategory,
    cancelSupportCategorySelection,
    startSupportChatSession,
    cancelWaitingForAgent,
    submitInChatTicket,
    searchUsers,
    getCurrentUserInfo,
    archiveConversation,
    unarchiveConversation,
    fetchArchivedConversations,
    askAssistant,
    clearAssistantPrompt,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// ============================================
// HOOK
// ============================================

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};
